import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { LanguageField, BriefingField, AutonomyField, WorkflowField, OutlookField, GroupsField } from "./fields";

const STEPS = ["language", "briefing", "autonomy", "workflows", "outlook", "groups"];

export default function Wizard({ onDone }) {
  const t = useT();
  const [step, setStep] = useState(0);
  const [settings, setSettings] = useState(null);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/coach/assistant/settings").then(({ data }) => setSettings(data)).catch(() => setSettings({}));
    api.get("/coach/assistant/groups").then(({ data }) => setGroups(data.groups || [])).catch(() => {});
  }, []);

  if (!settings) return <div className="space-y-4"><Skeleton className="h-16 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /></div>;

  const set = (patch) => setSettings((s) => ({ ...s, ...patch }));
  const cur = STEPS[step];

  const finish = async () => {
    setSaving(true);
    try {
      await api.put("/coach/assistant/settings", { ...settings, setup_done: true });
      toast.success(t("aipa.setup_done_toast"));
      onDone();
    } catch (err) { toast.error(apiError(err, t("aipa.checkout_error"))); setSaving(false); }
  };

  const titleKey = `aipa.step_${cur}`;
  const descKey = `aipa.step_${cur}_desc`;

  return (
    <div data-testid="aipa-wizard">
      <PageHeader eyebrow={t("aipa.title")} title={t("aipa.wizard_title")} subtitle={t("aipa.wizard_intro")} />
      <div className="rounded-2xl border border-border bg-card p-5">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("aipa.step_label", { n: step + 1, total: STEPS.length })}</p>
        <h2 className="font-display text-xl text-foreground">{t(titleKey)}</h2>
        <p className="mb-4 mt-1 text-sm text-muted-foreground">{t(descKey)}</p>
        <div className="mb-5">
          {cur === "language" && <LanguageField value={settings.lang || "de"} onChange={(v) => set({ lang: v })} />}
          {cur === "briefing" && <BriefingField settings={settings} set={set} />}
          {cur === "autonomy" && <AutonomyField settings={settings} set={set} />}
          {cur === "workflows" && <WorkflowField settings={settings} set={set} />}
          {cur === "outlook" && <OutlookField settings={settings} set={set} />}
          {cur === "groups" && <GroupsField settings={settings} set={set} groups={groups} />}
        </div>
        <div className="flex items-center justify-between">
          <Button variant="outline" className="gap-1.5 rounded-full" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="aipa-wizard-back">
            <ChevronLeft className="h-4 w-4" /> {t("aipa.back")}
          </Button>
          {step < STEPS.length - 1 ? (
            <Button className="gap-1.5 rounded-full" onClick={() => setStep((s) => s + 1)} data-testid="aipa-wizard-next">
              {t("aipa.next")} <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button className="gap-1.5 rounded-full" disabled={saving} onClick={finish} data-testid="aipa-wizard-finish">
              <Check className="h-4 w-4" /> {saving ? t("aipa.saving") : t("aipa.finish_setup")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
